/**
 * Dex Score — how HARD each collection is, against how big.
 *
 * Every other number on this page counts. Counting makes a poor contest: a
 * trainer three hundred behind on raw total may be sitting on the harder
 * collection, and a comparison page that only counts demotivates exactly the
 * person who most needs a reason to keep going. This is the card where they can
 * win.
 *
 * IT SHOWS ITS WORKING, on purpose. A score nobody can audit is a score nobody
 * believes, and the weights are a judgement call rather than a fact — so the
 * breakdown names which dexes earned it, and the note says plainly that the
 * numbers are about effort and not about odds. See @shared/dexRarity for why
 * this app will not print a shiny rate.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Trophy } from "lucide-react";

import { CompareSprite } from "@/components/trade/CompareSprite";
import { DEX_VARIANT_META, type DexVariantKey } from "@/components/dexVariantVisuals";
import { speciesName } from "@/data/speciesNames";
import { friendLineColor } from "@/lib/dexHistoryChart";
import { VARIANT_POINTS, type ScoredVariant } from "@shared/dexRarity";

interface ScoreSide {
  total: number;
  scored: number;
  byVariant: Array<{ variant: ScoredVariant; points: number; entries: number }>;
  rarest: Array<{ variant: string; dexNumber: number; points: number }>;
}

interface ScoreResponse {
  mine: ScoreSide;
  theirs: ScoreSide;
}

const EMPTY: ScoreSide = { total: 0, scored: 0, byVariant: [], rarest: [] };

/** The dexes worth naming in the "how it's scored" note, hardest first. */
const NOTABLE: ScoredVariant[] = ["shundo", "perfect", "shinyLucky", "shiny", "gmax"];

export function DexScoreCard({ friendId, friendName }: { friendId: number; friendName: string }) {
  const [open, setOpen] = useState(false);

  const url = `/api/friends/${friendId}/dex-score`;
  const q = useQuery<ScoreResponse>({ queryKey: [url], staleTime: 5 * 60 * 1000, retry: false });

  const mine = q.data?.mine ?? EMPTY;
  const theirs = q.data?.theirs ?? EMPTY;
  const mineColor = "var(--accent-fill)";
  const theirsColor = friendLineColor(friendId);
  const iLead = mine.total > theirs.total;
  const theyLead = theirs.total > mine.total;

  return (
    <section className="v2-neon-card-soft p-4 mt-4" data-testid="section-dex-score">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="v2-eyebrow" data-testid="eyebrow-dex-score">
          <span className="v2-eyebrow__dot" aria-hidden />
          Dex Score
        </span>
        <Trophy className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--accent-fill)" }} aria-hidden />
      </div>
      <p className="text-[10.5px] mb-3" style={{ color: "var(--text-secondary)" }}>
        Not how many you have, but how hard they are to achieve. Read how it's scored below for
        more info.
      </p>

      {q.isLoading ? (
        <p className="text-[11.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="dex-score-loading">
          Weighing both collections…
        </p>
      ) : q.isError ? (
        <p className="text-[11.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="dex-score-error">
          Couldn't work out the scores. Pull again in a moment.
        </p>
      ) : (
        <>
          {/* The headline, side by side. Only the leader is weighted, and a
              draw weights neither: a tie is not a win. */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end" data-testid="dex-score-totals">
            <ScoreSideView
              label="You"
              side={mine}
              color={mineColor}
              leading={iLead}
              testId="mine"
            />
            <span className="text-[10px] pb-1" style={{ color: "var(--text-tertiary)" }}>
              vs
            </span>
            <ScoreSideView
              label={friendName}
              side={theirs}
              color={theirsColor}
              leading={theyLead}
              testId="theirs"
            />
          </div>

          {/* The rarest thing each of you owns. This is the braggable half — a
              number is an argument, a sprite is a trophy. */}
          <div className="grid grid-cols-2 gap-2 mt-3" data-testid="dex-score-rarest">
            <RarestColumn entries={mine.rarest} color={mineColor} testId="mine" />
            <RarestColumn entries={theirs.rarest} color={theirsColor} testId="theirs" />
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="dex-score-explain-toggle"
            className="mt-3 w-full inline-flex items-center justify-center gap-1 text-[10px] font-semibold"
            style={{ color: "var(--text-tertiary)" }}
          >
            How it's scored
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>

          {open && (
            <div className="mt-2 text-[10px] leading-relaxed" style={{ color: "var(--text-tertiary)" }} data-testid="dex-score-explain">
              <p>
                Every catch is worth points for the dex it lands in, doubled for a Legendary and
                tripled for a Mythical:{" "}
                {NOTABLE.map((v, i) => (
                  <span key={v}>
                    {i > 0 ? " · " : ""}
                    <span style={{ color: DEX_VARIANT_META[v as DexVariantKey] ? `hsl(${DEX_VARIANT_META[v as DexVariantKey].token})` : undefined }}>
                      {DEX_VARIANT_META[v as DexVariantKey]?.label ?? v} {VARIANT_POINTS[v]}
                    </span>
                  </span>
                ))}
                {" · "}Main 1.
              </p>
              <p className="mt-1.5">
                These are about EFFORT, not odds. Nobody publishes catch rates, so we don't quote
                any. A 100% outranks a shiny here because shiny rates move enormously during
                events and a 100% never gets easier than its IV floor.
              </p>
              {mine.byVariant.length > 0 && (
                <p className="mt-1.5" data-testid="dex-score-my-breakdown">
                  Your points come mostly from{" "}
                  {mine.byVariant.slice(0, 3).map((b, i) => (
                    <span key={b.variant}>
                      {i > 0 ? ", " : ""}
                      {DEX_VARIANT_META[b.variant as DexVariantKey]?.label ?? b.variant} ({b.points})
                    </span>
                  ))}
                  .
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ScoreSideView({
  label,
  side,
  color,
  leading,
  testId,
}: {
  label: string;
  side: ScoreSide;
  color: string;
  leading: boolean;
  testId: string;
}) {
  return (
    <div className="min-w-0 text-center" data-testid={`dex-score-${testId}`} data-leading={leading || undefined}>
      <span className="block text-[10px] font-bold truncate" style={{ color }}>
        {label}
      </span>
      <span
        className="block tabular-nums leading-none"
        style={{
          color: leading ? color : "var(--text-secondary)",
          fontSize: leading ? 26 : 22,
          fontWeight: leading ? 800 : 600,
        }}
        data-testid={`dex-score-total-${testId}`}
      >
        {side.total.toLocaleString()}
      </span>
      <span className="block text-[9px] mt-0.5" style={{ color: "var(--text-tertiary)" }}>
        {side.scored.toLocaleString()} catches
      </span>
    </div>
  );
}

/** The hardest things one trainer owns — a trophy shelf, not a table. */
function RarestColumn({
  entries,
  color,
  testId,
}: {
  entries: ScoreSide["rarest"];
  color: string;
  testId: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-[9.5px] text-center" style={{ color: "var(--text-ink-faint)" }} data-testid={`dex-score-rarest-${testId}-empty`}>
        Nothing scored yet.
      </p>
    );
  }
  return (
    <ul className="space-y-1" data-testid={`dex-score-rarest-${testId}`}>
      {entries.map((e) => {
        const meta = DEX_VARIANT_META[e.variant as DexVariantKey];
        return (
          <li
            key={`${e.variant}:${e.dexNumber}`}
            className="flex items-center gap-1.5 rounded-lg px-1.5 py-1"
            style={{ background: "var(--surface-sunken)", border: `1px solid ${color}` }}
          >
            <span className="w-6 h-6 shrink-0 rounded-md overflow-clip inline-flex items-center justify-center">
              <CompareSprite id={e.dexNumber} shiny={e.variant.toLowerCase().includes("shiny") || e.variant === "shundo"} size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[9.5px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                {speciesName(e.dexNumber)}
              </span>
              <span className="block text-[8px] truncate" style={{ color: meta ? `hsl(${meta.token})` : "var(--text-tertiary)" }}>
                {meta?.label ?? e.variant}
              </span>
            </span>
            <span className="text-[10px] font-bold tabular-nums shrink-0" style={{ color }}>
              {e.points}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
